import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { IconSync } from '@iwoca/icons';
import { Button, Pill } from '@iwoca/orion';

import styles from '../SpendingLimitDrawer.module.css';
import { UnavailableLimit } from './UnavailableLimit';
import { UpdateTurnover } from './UpdateTurnover';
import {
    TSpendingLimits,
    TSpendingLimitType
} from '../../hooks/useGetSpendingLimits';
import { useUpdateTurnover } from '../../hooks/useUpdateTurnover';
import { formatDuration } from '../../spendingLimitCard/components/formatDuration';

export const ExpiredLimitDrawer = ({
    spendingLimits,
    refreshSpendingLimit,
    loadingRefreshSpendingLimit,
    isError
}: {
    spendingLimits: TSpendingLimits;
    refreshSpendingLimit: () => Promise<void>;
    loadingRefreshSpendingLimit: boolean;
    isError: boolean;
}) => {
    const hasThirtyDaysLimit = Boolean(spendingLimits.limits['THIRTY_DAYS']);
    const { shouldUpdateTurnover } = useUpdateTurnover();

    return (
        <>
            <div className={styles.spendingLimits}>
                {Object.keys(spendingLimits.limits)
                    .filter((spendingLimitKey) => {
                        // Exclude THIRTY_DAYS limit
                        // Same limit as three months
                        return spendingLimitKey !== 'THIRTY_DAYS';
                    })
                    .map((key) => {
                        const spendingLimit =
                            spendingLimits.limits[key as TSpendingLimitType];

                        if (!spendingLimit)
                            return (
                                <UnavailableLimit
                                    sellerProduct={key as TSpendingLimitType}
                                    key={key}
                                />
                            );

                        return (
                            <div key={key}>
                                <div className={styles.loanDuration}>
                                    {formatDuration({
                                        duration:
                                            spendingLimit.duration_in_months,
                                        groupSameLimits: hasThirtyDaysLimit
                                    })}{' '}
                                    purchases
                                </div>
                                <div className={styles.leftToSpend}>
                                    Left to spend:
                                </div>
                                <div className={styles.creditLimitContainer}>
                                    <FormattedCurrency
                                        className={styles.creditLimit}
                                        value={spendingLimit.available_amount}
                                    />
                                    <Pill className={styles.estimatedPill}>
                                        Estimated
                                    </Pill>
                                </div>
                                <div className={styles.maxLimit}>
                                    Estimated spending limit:{' '}
                                    <FormattedCurrency
                                        value={spendingLimit.max_amount}
                                    />
                                </div>
                            </div>
                        );
                    })}
                <div>
                    {shouldUpdateTurnover && (
                        <UpdateTurnover
                            refreshSpendingLimit={refreshSpendingLimit}
                            loadingRefreshSpendingLimit={
                                loadingRefreshSpendingLimit
                            }
                        />
                    )}
                    {!shouldUpdateTurnover && (
                        <Button
                            disabled={loadingRefreshSpendingLimit}
                            onClick={refreshSpendingLimit}
                            className={styles.refreshButton}
                        >
                            {loadingRefreshSpendingLimit ? (
                                <>
                                    <IconSync className={styles.spinningIcon} />
                                    Checking limits...
                                </>
                            ) : (
                                <>
                                    <IconSync />
                                    Secure my limits
                                </>
                            )}
                        </Button>
                    )}
                    {isError && (
                        <div className={styles.errorMessage}>
                            Something went wrong
                        </div>
                    )}
                </div>
            </div>
            <div className={styles.footer}>
                <div>
                    *Your spending limits are subject to approval and reassessed
                    every 30 days - if something changes with your business the
                    amounts might change.
                </div>
            </div>
        </>
    );
};
